/**
 * Mirrored from @nacs/shared `auth/permissions.ts` (ADR-0006). Same deviation note as contracts.ts:
 * replace with the package import once it is published. The nav inventory test locks nav gates to this catalog.
 */
export const PERMISSION_CATALOG = {
  overview: { label: "Overview", actions: ["view"] },
  devices: { label: "Devices", actions: ["view", "manage"] },
  doors: { label: "Doors", actions: ["view", "control"] },
  people: { label: "People", actions: ["view", "edit", "import"] },
  events: { label: "Events", actions: ["view", "export"] },
  /**
   * M9 §4: attendance built from the per-person rollup. Separate from `events` because it is a
   * different question about the same rows — a Security Officer who watches the door log also answers
   * "was Asha in yesterday", while a Viewer may read it but not carry the month out of the building.
   */
  reports: { label: "Reports", actions: ["view", "export"] },
  /**
   * M9 §5: weekly time templates and holiday lists. Organisation-wide by nature — a schedule belongs
   * to the organisation, not to a terminal — so a device-scoped role may read them (it has to, to
   * understand a person's device card) and only `schedules:manage` changes them.
   */
  schedules: { label: "Schedules", actions: ["view", "manage"] },
  /**
   * M14 (ADR-0019 decision 7): the organisation charging its own people — gym dues, a canteen
   * wallet, a co-working plan, school fees. NOT `billing` (Nexborg charging the organisation).
   * `record` is the front desk's (a charge, a payment, its receipt); `adjust` is the correction —
   * an adjustment, a refund, a write-off, a reversal.
   */
  ledger: { label: "Ledger", actions: ["view", "record", "adjust"] },
  /**
   * M15 (ADR-0017 decision 8): the gym module's own surfaces — registering a member, selling a plan,
   * freezing it, assigning a trainer. Two actions, not three: unlike `ledger` above, taking money and
   * letting somebody off are not both gym powers — the money a membership causes is the LEDGER's.
   *
   * The `gym` catalogue item unlocks BOTH this module and `ledger`, which is the "one item, several
   * permission modules" shape decision 8 exists for. The portal reads it the way the api does: a
   * workspace entry is visible when the add-on is live AND the role holds the permission.
   */
  gym: { label: "Gym", actions: ["view", "manage"] },
  /**
   * M16 wave 1 (ADR-0021): passes — a time-bounded grant plus a chosen set of credentials. Its own
   * module rather than `people:edit`, because issuing a pass MINTS A DOOR-OPENING CREDENTIAL and
   * hands it to somebody who is not on the staff list; revoking is the safer half and rides with it.
   */
  passes: { label: "Passes", actions: ["view", "manage"] },
  /**
   * M16 wave 2 (ADR-0021 decision 9): the visitor register. Separate from `passes` because the two
   * answer different questions — "may this role see who has been in the building" and "may this role
   * cut a code that opens a door" — and issuing needs both.
   */
  visitors: { label: "Visitors", actions: ["view", "manage"] },
  /**
   * M19 (contracts §4, ADR-0023): the Staff attendance module. Six actions, because HR's powers really
   * are six different people's: `manage` keeps the employee master and the roster, `correct` fixes a
   * day (a punch, an ignore, an override, a month adjustment), `approve` decides other people's
   * requests and punches, `close` freezes a month for payroll, and `export` carries it out of the
   * building — the only `staff:*` an API key may hold. An employee's power over their OWN record is
   * not here at all: it follows from the membership's link to the person (`/me/staff`), never from a
   * permission somebody ticked.
   */
  staff: { label: "Staff", actions: ["view", "manage", "correct", "approve", "close", "export"] },
  /**
   * M20 (contracts §4, ADR-0025): the Student Attendance add-on. `manage` keeps the student master,
   * groups and calendar; `correct` fixes a student's day (and records leave); `export` carries a
   * register out; `sms` sets the per-group parent-SMS policy. No API key may hold any of them in v1.
   */
  students: { label: "Students", actions: ["view", "manage", "correct", "export", "sms"] },
  /**
   * M24 (contracts §4, ADR-0026): Face Kiosk — a phone at a gate standing in for a terminal. `manage` keeps
   * the seats and the switch, `approve` decides the punches nobody's face vouched for, `export` carries the
   * Scans sheet out. No new action word: all four already exist, so no grid column moves. Holding a seat
   * needs none of them (the api checks the seat, not the role), and no API key may hold any of them in v1.
   */
  face_kiosk: { label: "Face Kiosk", actions: ["view", "manage", "approve", "export"] },
  /**
   * M25 (contracts §4, ADR-0027): Labour attendance — contractors, sites, labourers and their days. `manage`
   * keeps the masters and the labourers (the identity tier is shown to it alone), `correct` changes a day
   * and is held by nothing else (no other action implies it), `lock` closes days up to a date, `export`
   * carries a register out — the only `labour:*` an API key may hold. `lock` is a NEW action word: the
   * portal's `PERMISSION_ACTIONS` gains a column for it.
   */
  labour: { label: "Labour", actions: ["view", "manage", "correct", "lock", "export"] },
  /**
   * M26 (contracts §4, ADR-0031): Payroll — the masters, the month's run and its money. `manage` keeps
   * components, templates, structures and statutory details; `adjust` the month's inputs; `run` computes a month
   * (and excludes, includes, remarks); `close` closes, reopens and publishes; `approve` decides tax declarations;
   * `pay` sees bank accounts and records payments; `export` carries the register out — the only `payroll:*` an
   * API key may hold. `run` and `pay` are NEW action words: the portal's `PERMISSION_ACTIONS` gains two columns.
   */
  payroll: {
    label: "Payroll",
    actions: ["view", "manage", "adjust", "run", "close", "approve", "pay", "export"],
  },
  /**
   * M21 (contracts §6, ADR-0028, ADR-0029 d22): Co-working — an operator selling seats on contracts, billing
   * them and opening doors for them. `manage` runs companies, members, seats, products and the module's
   * settings; `invoicing` issues and corrects the organisation's tax documents (with `ledger:record` and
   * `ledger:adjust` for the money); `contracts` signs, ends, renews and changes contracts and blocks for
   * non-payment; `export` carries a report or a document out. `invoicing` and `contracts` are NEW action
   * words: the portal's `PERMISSION_ACTIONS` gains two columns. No API key may hold any of them in R1.
   */
  coworking: { label: "Co-working", actions: ["view", "manage", "invoicing", "contracts", "export"] },
  /**
   * M27 (ADR-0030 addendum 2026-10-28, contracts §4): Rooms and Equipment. `manage` runs resources, doors, booking QR,
   * booker lists, displays, app access and the settings; `book` creates bookings and changes their attendees; `cancel`
   * cancels a booking; `export` carries a report out. `book` and `cancel` are NEW action words: the portal's
   * `PERMISSION_ACTIONS` gains two columns. No API key may hold any of them in v1.
   */
  rooms: { label: "Rooms and Equipment", actions: ["view", "manage", "book", "cancel", "export"] },
  /**
   * M20 (ADR-0024): the organisation's shared SMS balance. Core, not an add-on: every module that
   * texts somebody draws on the same wallet. Recharging also needs `billing:manage`.
   */
  sms: { label: "SMS", actions: ["view", "manage"] },
  members: { label: "Members & roles", actions: ["view", "manage"] },
  api_keys: { label: "API keys", actions: ["manage"] },
  /** M7 (ADR-0013): an integration surface, not a money surface — Admin holds it, and API keys may. */
  webhooks: { label: "Webhooks", actions: ["view", "manage"] },
  settings: { label: "Settings", actions: ["view", "manage"] },
  billing: { label: "Plan & billing", actions: ["view", "manage"] },
} as const;

export type PermissionModule = keyof typeof PERMISSION_CATALOG;
/** Full access is "*", never every box ticked. */
export type PermissionSet = "*" | readonly string[];

export const PERMISSION_MODULES = Object.keys(PERMISSION_CATALOG) as PermissionModule[];
/**
 * Every action that appears in the catalog, in a stable column order (view first, then the write actions).
 *
 * M19 appends the three staff actions at the END, so every existing column keeps its place. They must be
 * here and not only in the catalog: the role grid builds its columns from this list and normalises a role
 * through it, so an action missing from it is one a saved role silently loses.
 */
export const PERMISSION_ACTIONS = [
  "view",
  "manage",
  "edit",
  "import",
  "control",
  "export",
  "record",
  "adjust",
  "correct",
  "approve",
  "close",
  // M20: the students module's parent-SMS policy action, appended so every older column keeps its place.
  "sms",
  // M25: the labour module's day lock ("Lock days"), appended last for the same reason.
  "lock",
  // M26: the payroll module's two new words ("Run payroll", "Pay salaries"), appended for the same reason.
  "run",
  "pay",
  // M21: the co-working module's two new words ("Issue invoices", "Contracts"), appended for the same reason.
  "invoicing",
  "contracts",
  // M27: Rooms and Equipment's two new words ("Book", "Cancel bookings"), appended for the same reason.
  "book",
  "cancel",
] as const;

/**
 * Actions that imply "view" of the same module (M19 contracts §4: `correct`, `approve` and `close` join
 * it; M20 contracts §4: `sms` joins it — `correct` and `export` already did; M25 contracts §4: `lock`).
 */
export const IMPLIES_VIEW: ReadonlySet<string> = new Set([
  "manage",
  "edit",
  "import",
  "control",
  "export",
  "record",
  "adjust",
  "correct",
  "approve",
  "close",
  "sms",
  "lock",
  // M26 contracts §4: `run` and `pay` act on the month they read (`PAYROLL_IMPLIES_VIEW` on the server).
  "run",
  "pay",
  // M21 contracts §6: `invoicing` and `contracts` act on what they read (`COWORKING_IMPLIES_VIEW` on the server).
  "invoicing",
  "contracts",
  // M27 contracts §4: `book` and `cancel` act on the calendar they read (`ROOMS_IMPLIES_VIEW` on the server).
  "book",
  "cancel",
]);

export function hasPermission(perms: PermissionSet, module: PermissionModule, action: string): boolean {
  if (perms === "*") return true;
  if (perms.includes(`${module}:${action}`)) return true;
  if (action === "view")
    return perms.some((p) => p.startsWith(`${module}:`) && IMPLIES_VIEW.has(p.split(":")[1] ?? ""));
  return false;
}

/** M20 (contracts §3): which students a role reaches — every group, or only the groups it teaches. */
export const STUDENT_SCOPES = ["all", "assigned"] as const;
export type StudentScope = (typeof STUDENT_SCOPES)[number];

/**
 * M25 (contracts §3, §4): which labourers a role reaches — every one, only those at the sites the member is
 * assigned, or only those under the contractors the member is assigned. A custom role takes any of the
 * three; `*` (full access) is always `all`.
 */
export const LABOUR_SCOPES = ["all", "sites", "contractors"] as const;
export type LabourScope = (typeof LABOUR_SCOPES)[number];

/** Role presets are the primary path; custom roles live behind "Advanced" (ADR-0006). Keyed by the API's `key`. */
export const ROLE_PRESETS: Record<
  string,
  { name: string; description: string; studentScope?: StudentScope; labourScope?: LabourScope }
> = {
  owner: { name: "Owner", description: "Full access, including billing and deleting the organisation" },
  admin: { name: "Admin", description: "Everything except billing" },
  /**
   * M14 (ADR-0019 decision 7): the desk. Takes payments and issues receipts, looks people up and
   * edits them, reads the day's reports — and cannot correct the ledger (`adjust` stays an owner's).
   */
  front_desk: { name: "Front desk", description: "Takes payments, issues receipts, looks people up" },
  security_officer: { name: "Security officer", description: "Live events, door control, people lookup" },
  viewer: { name: "Viewer", description: "Read-only" },
  /**
   * M19 (contracts §4): HR runs the employees, their days and their requests — everything in Staff except
   * closing a month, which stays with whoever signs off payroll.
   */
  hr: {
    name: "HR",
    description: "Employees, attendance, leave and approvals; everything in Staff except closing a month",
  },
  /**
   * M19 (ADR-0023 decision 4): an employee who signs in only to punch and to see their own attendance.
   * The preset holds NO permission — what an employee may do for themselves follows from being linked to
   * their own person, not from a role — and it is read-only so nobody can edit it into something more.
   */
  employee: {
    name: "Employee",
    description: "Signs in to the mobile app for their own attendance and requests; sees nothing else",
  },
  /**
   * M20 (contracts §4, ADR-0025 decision 7): a class teacher. Reads and corrects the attendance of the
   * students in the groups they are assigned to (`students:view|correct`, scope `assigned`), and
   * nothing outside them — a student of another group answers "not found", never "forbidden".
   */
  teacher: {
    name: "Teacher",
    description: "Attendance of their assigned groups: reads it and corrects it",
    studentScope: "assigned",
  },
  /**
   * M25 (contracts §4, ADR-0027 decision 12): the site supervisor registers and manages the labourers of the
   * sites they are assigned and reads and exports their attendance — and cannot change a day: no
   * `labour:correct`, the line between the person who runs the site and the person who edits the record.
   */
  site_supervisor: {
    name: "Site supervisor",
    description: "Labourers, contractors and today's attendance of their sites; cannot change a day",
    labourScope: "sites",
  },
  /** M25 (L18): fixes a day — an added punch, an ignored punch, an override — at their assigned sites. */
  attendance_editor: {
    name: "Attendance editor",
    description: "Corrects the labour attendance of their sites",
    labourScope: "sites",
  },
  /** M25 (decision 12): a contractor's own view — their labourers' attendance, nothing else. */
  contractor: {
    name: "Contractor",
    description: "Reads and exports the attendance of their own contractor's labourers",
    labourScope: "contractors",
  },
  /**
   * M21 (contracts §6): the three Co-working presets, re-synced to every organisation by `roles:resync`. The
   * community manager runs the centre — all of Co-working and the desk's money, but no `ledger:adjust` (a credit
   * note, a release or a write-off is the accounts' power); Accounts issues and corrects documents and takes
   * money and cannot end a contract or Block a door; the external accountant reads and exports.
   */
  community_manager: {
    name: "Community manager",
    description:
      "Runs the co-working centre: members, contracts, seats, payments and the doors; no corrections",
  },
  accounts: {
    name: "Accounts",
    description: "Co-working invoices, notes, payments and deposits; cannot end a contract or block a door",
  },
  external_accountant: {
    name: "External accountant",
    description: "Read-only, plus exporting Co-working's reports and documents",
  },
};
